import { useCallback, useEffect, useRef, useState } from 'react';

/** Value that trails `value` by `delay` ms — for search boxes that hit the API. */
export function useDebounce<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

export interface DebouncedCallback<A extends unknown[]> {
  (...args: A): void;
  cancel: () => void;
  flush: () => void;
}

/**
 * Stable debounced callback. The identity never changes, so it is safe in a
 * dependency array even when the wrapped function closes over fresh state.
 */
export function useDebouncedCallback<A extends unknown[]>(
  fn: (...args: A) => void,
  delay = 250,
): DebouncedCallback<A> {
  const latest = useRef(fn);
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef<A | undefined>(undefined);

  useEffect(() => {
    latest.current = fn;
  }, [fn]);

  const cancel = useCallback(() => {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
    pending.current = undefined;
  }, []);

  const flush = useCallback(() => {
    const args = pending.current;
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
    pending.current = undefined;
    if (args) latest.current(...args);
  }, []);

  useEffect(() => cancel, [cancel]);

  const run = useCallback(
    (...args: A) => {
      pending.current = args;
      if (timer.current !== undefined) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = undefined;
        pending.current = undefined;
        latest.current(...args);
      }, delay);
    },
    [delay],
  );

  const debounced = run as DebouncedCallback<A>;
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}
